import { useState } from 'react';
import { CaseSensitive, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const TextCaseConverter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const toUpperCase = () => {
    setOutput(input.toUpperCase());
    toast.success('Converted to UPPERCASE');
  };

  const toLowerCase = () => {
    setOutput(input.toLowerCase());
    toast.success('Converted to lowercase');
  };

  const toTitleCase = () => {
    const result = input
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    setOutput(result);
    toast.success('Converted to Title Case');
  };

  const toSentenceCase = () => {
    const result = input
      .toLowerCase()
      .replace(/(^\s*\w|[.!?]\s*\w)/g, match => match.toUpperCase());
    setOutput(result);
    toast.success('Converted to Sentence case');
  };

  const toAlternatingCase = () => {
    const result = input
      .split('')
      .map((char, i) => i % 2 === 0 ? char.toLowerCase() : char.toUpperCase())
      .join('');
    setOutput(result);
    toast.success('Converted to aLtErNaTiNg CaSe');
  };

  const toInverseCase = () => {
    const result = input
      .split('')
      .map(char => {
        if (char === char.toUpperCase()) return char.toLowerCase();
        return char.toUpperCase();
      })
      .join('');
    setOutput(result);
    toast.success('Converted to inverse case');
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <CaseSensitive className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Text Case Converter</h1>
          <p className="text-muted-foreground">
            Convert text between different cases
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle>Input Text</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Enter text to convert..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="min-h-[200px] resize-none"
            />
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={toUpperCase} disabled={!input.trim()} variant="outline">
                UPPERCASE
              </Button>
              <Button onClick={toLowerCase} disabled={!input.trim()} variant="outline">
                lowercase
              </Button>
              <Button onClick={toTitleCase} disabled={!input.trim()} variant="outline">
                Title Case
              </Button>
              <Button onClick={toSentenceCase} disabled={!input.trim()} variant="outline">
                Sentence case
              </Button>
              <Button onClick={toAlternatingCase} disabled={!input.trim()} variant="outline">
                aLtErNaTiNg
              </Button>
              <Button onClick={toInverseCase} disabled={!input.trim()} variant="outline">
                InVeRsE
              </Button>
            </div>
            <Button variant="ghost" onClick={clearAll} className="w-full">
              Clear All
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Result</span>
              {output && (
                <Button variant="ghost" size="sm" onClick={copyOutput} className="gap-2">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Copy
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Converted text will appear here..."
              value={output}
              readOnly
              className="min-h-[200px] resize-none bg-muted/30"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TextCaseConverter;